/**
 * UserBoard Component
 * Displays user call history and usage statistics from GET /api/user/calls and /api/user/usage (auth API).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  PhoneCall,
  Clock,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  BarChart3,
  PieChart,
} from 'lucide-react';
import { agentApi } from '../services/api';

function effectiveDurationMs(call) {
  if (call == null) return null;
  if (call.duration != null && Number(call.duration) > 0) return Number(call.duration);
  if (call.callStartTime && call.callEndTime) {
    const s = new Date(call.callStartTime).getTime();
    const e = new Date(call.callEndTime).getTime();
    if (Number.isFinite(s) && Number.isFinite(e) && e >= s) return e - s;
  }
  return null;
}

function computeUsageFromCalls(calls) {
  const totalCalls = calls.length;
  const totalDuration = calls.reduce((sum, call) => {
    const ms = effectiveDurationMs(call);
    return sum + (ms != null ? ms : 0);
  }, 0);
  const withDuration = calls.filter((c) => effectiveDurationMs(c) != null).length;
  const averageDuration = withDuration > 0 ? Math.round(totalDuration / withDuration) : 0;

  const outcomeCounts = {
    success: calls.filter((c) => c.outcome === 'success').length,
    failure: calls.filter((c) => c.outcome === 'failure').length,
    unknown: calls.filter((c) => !c.outcome || (c.outcome !== 'success' && c.outcome !== 'failure')).length,
  };

  const moodCounts = {
    positive: calls.filter((c) => c.mood === 'positive').length,
    neutral: calls.filter((c) => c.mood === 'neutral').length,
    negative: calls.filter((c) => c.mood === 'negative').length,
    unknown: calls.filter((c) => !c.mood || !['positive', 'neutral', 'negative'].includes(c.mood)).length,
  };

  const scriptCounts = {};
  calls.forEach((call) => {
    const scriptName = call.scriptName || 'Unknown';
    scriptCounts[scriptName] = (scriptCounts[scriptName] || 0) + 1;
  });

  const dailyCalls = {};
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  calls
    .filter((call) => call.createdAt && new Date(call.createdAt) >= thirtyDaysAgo)
    .forEach((call) => {
      const date = new Date(call.createdAt).toISOString().split('T')[0];
      dailyCalls[date] = (dailyCalls[date] || 0) + 1;
    });

  return {
    totalCalls,
    totalDuration,
    totalDurationMinutes: Math.round(totalDuration / 60000),
    averageDuration,
    averageDurationMinutes: Math.round(averageDuration / 60000),
    outcomeCounts,
    moodCounts,
    scriptCounts,
    dailyCalls,
  };
}

const UserBoard = () => {
  const [calls, setCalls] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(30);
  const [selectedCall, setSelectedCall] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const endDate = new Date().toISOString();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      const rangeParams = {
        startDate: startDate.toISOString(),
        endDate,
      };

      const { data: callsData } = await agentApi.get('/api/user/calls', {
        params: { limit: 100, ...rangeParams },
      });

      if (!callsData?.success) {
        throw new Error(callsData?.message || 'Failed to load calls');
      }

      const list = callsData.calls || [];
      setCalls(list);

      try {
        const { data: usageData } = await agentApi.get('/api/user/usage', { params: rangeParams });
        if (usageData?.success && usageData.stats) {
          setUsage(usageData.stats);
        } else {
          setUsage(computeUsageFromCalls(list));
        }
      } catch {
        setUsage(computeUsageFromCalls(list));
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Failed to load dashboard data';
      setError(msg);
      setCalls([]);
      setUsage(null);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatDuration = (ms) => {
    if (ms == null || ms === '') return '—';
    const n = Number(ms);
    if (!Number.isFinite(n) || n < 0) return '—';
    if (n === 0) return '0:00';
    const totalSeconds = Math.floor(n / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getOutcomeIcon = (outcome) => {
    switch (outcome) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failure':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getMoodColor = (mood) => {
    switch (mood) {
      case 'positive':
        return 'text-green-600 bg-green-50';
      case 'negative':
        return 'text-red-600 bg-red-50';
      case 'neutral':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-freshroad-teal mx-auto mb-4" />
          <p className="text-gray-600">Loading your call data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-freshroad-black mb-2">My Call Dashboard</h1>
            <p className="text-gray-600">Track your calls and usage statistics</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm flex items-start gap-2">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Could not load dashboard data</p>
            <p className="mt-1">{error}</p>
          </div>
        </div>
      )}

      <div className="mb-6 flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700">Date Range:</label>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(Number(e.target.value))}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-freshroad-teal focus:border-transparent"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {usage && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Calls</p>
                <p className="text-3xl font-bold text-freshroad-black">{usage.totalCalls}</p>
              </div>
              <div className="p-3 bg-freshroad-gradient-teal rounded-lg">
                <PhoneCall className="w-6 h-6 text-freshroad-teal" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Duration</p>
                <p className="text-3xl font-bold text-freshroad-black">{usage.totalDurationMinutes}m</p>
                <p className="text-xs text-gray-500 mt-1">Avg: {Math.round(usage.averageDurationMinutes)}m</p>
              </div>
              <div className="p-3 bg-freshroad-gradient-sky rounded-lg">
                <Clock className="w-6 h-6 text-freshroad-sky-blue" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Success Rate</p>
                <p className="text-3xl font-bold text-freshroad-black">
                  {usage.totalCalls > 0
                    ? Math.round((usage.outcomeCounts.success / usage.totalCalls) * 100)
                    : 0}
                  %
                </p>
                <p className="text-xs text-gray-500 mt-1">{usage.outcomeCounts.success} successful</p>
              </div>
              <div className="p-3 bg-freshroad-gradient-green rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Positive Mood</p>
                <p className="text-3xl font-bold text-freshroad-black">{usage.moodCounts.positive}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {usage.totalCalls > 0
                    ? Math.round((usage.moodCounts.positive / usage.totalCalls) * 100)
                    : 0}
                  % of calls
                </p>
              </div>
              <div className="p-3 bg-freshroad-gradient-blue rounded-lg">
                <CheckCircle className="w-6 h-6 text-freshroad-blue" />
              </div>
            </div>
          </div>
        </div>
      )}

      {usage && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-freshroad-black mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-freshroad-teal" />
              Call Outcomes
            </h3>
            <div className="space-y-3">
              {['success', 'failure', 'unknown'].map((key) => {
                const label = key === 'unknown' ? 'Unknown' : key.charAt(0).toUpperCase() + key.slice(1);
                const count = usage.outcomeCounts[key] ?? 0;
                const pct = usage.totalCalls > 0 ? (count / usage.totalCalls) * 100 : 0;
                const barColor = key === 'success' ? 'bg-green-500' : key === 'failure' ? 'bg-red-500' : 'bg-gray-400';
                return (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {key === 'success' && <CheckCircle className="w-4 h-4 text-green-500" />}
                      {key === 'failure' && <XCircle className="w-4 h-4 text-red-500" />}
                      {key === 'unknown' && <AlertCircle className="w-4 h-4 text-gray-400" />}
                      <span className="text-sm text-gray-700">{label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div className={`${barColor} h-2 rounded-full`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-12 text-right">{count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-freshroad-black mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-freshroad-teal" />
              Call Moods
            </h3>
            <div className="space-y-3">
              {[
                { key: 'positive', dot: 'bg-green-500', bar: 'bg-green-500' },
                { key: 'neutral', dot: 'bg-blue-500', bar: 'bg-blue-500' },
                { key: 'negative', dot: 'bg-red-500', bar: 'bg-red-500' },
              ].map(({ key, dot, bar }) => {
                const count = usage.moodCounts[key] ?? 0;
                const pct = usage.totalCalls > 0 ? (count / usage.totalCalls) * 100 : 0;
                return (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${dot}`} />
                      <span className="text-sm text-gray-700 capitalize">{key}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div className={`${bar} h-2 rounded-full`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-12 text-right">{count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-freshroad-black flex items-center gap-2">
            <FileText className="w-5 h-5 text-freshroad-teal" />
            Recent Calls
          </h2>
        </div>
        <div className="overflow-x-auto">
          {calls.length === 0 ? (
            <div className="p-12 text-center">
              <PhoneCall className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No calls found for the selected date range</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Script
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Outcome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mood
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Summary
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {calls.map((call) => (
                  <tr
                    key={call.id || call.callSid}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedCall(call)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(call.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {call.scriptName || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDuration(effectiveDurationMs(call))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getOutcomeIcon(call.outcome)}
                        <span className="text-sm text-gray-700 capitalize">{call.outcome || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMoodColor(call.mood)}`}>
                        {call.mood || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div className="max-w-xs truncate">{call.summary || 'No summary available'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selectedCall && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-freshroad-black">Call Details</h3>
              <button
                type="button"
                onClick={() => setSelectedCall(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Date</p>
                  <p className="text-base font-medium">{formatDate(selectedCall.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Duration</p>
                  <p className="text-base font-medium">{formatDuration(effectiveDurationMs(selectedCall))}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Script</p>
                  <p className="text-base font-medium">{selectedCall.scriptName || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Call SID</p>
                  <p className="text-base font-medium font-mono text-xs">{selectedCall.callSid || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Outcome</p>
                  <div className="flex items-center gap-2">
                    {getOutcomeIcon(selectedCall.outcome)}
                    <span className="text-base font-medium capitalize">{selectedCall.outcome || 'Unknown'}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Mood</p>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getMoodColor(selectedCall.mood)}`}
                  >
                    {selectedCall.mood || 'Unknown'}
                  </span>
                </div>
              </div>
              {selectedCall.summary && (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Summary</p>
                  <p className="text-base text-gray-900">{selectedCall.summary}</p>
                </div>
              )}
              {selectedCall.transcript && (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Transcript</p>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedCall.transcript}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserBoard;
